import moment from "moment";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// PTO approvals describing its own filter bar. It opens on pending requests, which is why a request
// that names no status leaves that alone.
declareAiCapability({
    id: "ptoApprovals.filter",
    title: "Filter the PTO approvals list",
    effect: "prefill",
    description:
        "Sets the filters on Payroll > PTO Approvals and re-runs its search: the date range, office, the request's status, the pay code, and a free-text search over the table. The page opens on pending requests. Only works while that page is open.",
    location: "the filter bar of the Payroll > PTO Approvals page",
    scope: "page",
    permissionKey: "view_caregivers_section_pto_approvals",
    params: [
        { name: "from", type: "date", required: false, description: "First day to include, as yyyy-MM-dd." },
        { name: "to", type: "date", required: false, description: "Last day to include, as yyyy-MM-dd." },
        {
            name: "status",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getPtoStatusOptions(),
            description: "Whether the request is pending, approved or declined."
        },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getPtoOfficeOptions(),
            description: "Limit to these offices."
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description: "Free text the table searches over every column, including caregiver name."
        }
    ],
    examples: ["approved PTO requests", "pending PTO for the europe office", "PTO approvals last month"]
});

let ptoScope = () => null;

function getPtoStatusOptions() {
    return (ptoScope()?.statuses ?? []).map((status) => ({
        value: String(status.id),
        label: String(status.text ?? status.id)
    }));
}

function getPtoOfficeOptions() {
    return (ptoScope()?.officeDataManager?.getOffices() ?? []).map((office) => ({
        value: String(office.id),
        label: office.name
    }));
}

//! @ngInject
export function ptoApprovalsCtrl(
  $scope,
  $rootScope,
  patientLookupService,
  $timeout,
  DatabaseApi,
  selectionLogic,
  NgTableParams,
  wildcard,
  FilterUtils,
  offices,
  ptoNotesModalService,
  $filter,
  itemSearchPageManager,
  $uibModal,
  toaster,
  $state,
) {

  function initialize() {
    $scope.tableParams = null;
    initFilters();
    initPageManager();
    initSelection();
    $scope.pageManager.resetSearchParamsByUrl();
    if ($scope.storageFilters !== undefined) {
      $scope.pageManager.setSearchParams($scope.storageFilters);
    }

    loadItemsIfAssetsAreReady();
  }

  const initFilters = () => {
    $scope.globalFilter = { val: "" };
    $scope.offices = offices;
    $scope.extraFilters = {
      payCodes: [],
      statuses: [{ id: "PENDING" }]
    };
    $scope.payCodesExtraSettings = {
      styleActive: true,
      displayProp: 'displayId',
      enableSearch: true,
      scrollable: true,
      scrollableHeight: "400px",
      
    };
    $scope.statuses = [
      { btnClass: 'danger', text: 'Declined', id: 'DECLINED' },
      { btnClass: 'warning', text: 'Pending', id: 'PENDING' },
      { btnClass: 'primary', text: 'Approved', id: 'APPROVED' }
    ];
    $scope.statusesExtraSettings = {
      styleActive: true,
      displayProp: 'text'
    };
    // Load cached filters
    $scope.storageFilters = FilterUtils.rootFilters.ptoApprovalsCtrl;
  };

  $scope.$on("$destroy", function () {
    const filters = $scope.pageManager.searchParams;
    FilterUtils.rootFilters.ptoApprovalsCtrl = angular.copy(filters);
  });

  const initPageManager = function () {
    const convertObjsArrToIdsStringSplitWithPipe = (objsArr) => objsArr.map(obj => obj.id).join('|');
    const convertIdsStringToObjsArr = (strCommasSeparated) => strCommasSeparated.split('|');

    $scope.pageManager = itemSearchPageManager.createSearchPageManager(
      "/caregiver_pto_instances"
    );

    $scope.officeDataManager = $scope.pageManager.getOfficeDataManager();
    $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();

    $scope.pageManager.initFromToDateParams();
    $scope.pageManager.initOfficeParam();
    $scope.pageManager.initSearchParamDeprecated("payCodes", $scope.extraFilters.payCodes, "payCodes",
      convertObjsArrToIdsStringSplitWithPipe, convertIdsStringToObjsArr);
    $scope.pageManager.initSearchParamDeprecated("statuses", $scope.extraFilters.statuses, "statuses",
      convertObjsArrToIdsStringSplitWithPipe, convertIdsStringToObjsArr);
  };

  const initSelection = function () {
    $scope.selectionLogic = selectionLogic.createNewLogic(function (item) {
      $scope.selectionLogic.addItemToCollection(item);
    });
  };

  const findField = (array, field, id) => {
    const objFound = array.find(obj => obj.id === id);
    if (!objFound) {
      return "";
    }

    return objFound[field];
  }

  const mapItems = function (items) {
    items.forEach(function (item) {
      item.office = findField($scope.offices, 'name', item.visitInstance.officeId);
      item.labelText = findField($scope.caregiverPTOLabels, 'text', item.labelId);
      item.payrollCode = findField($scope.payrollCodes, 'displayId', item.visitInstance.payrollCodeId);
      if (item.pastPtoStatus) {
        item.pastPtoStatus.currentYearMinutesUsage = Math.round(item.pastPtoStatus.currentYearMinutesUsage);
        item.pastPtoStatus.currentYearMinutesPotential = Math.round(item.pastPtoStatus.currentYearMinutesPotential);
        item.pastPtoStatus.totalMinutesUsage = Math.round(item.pastPtoStatus.totalMinutesUsage);
        item.pastPtoStatus.totalMinutesPotential = Math.round(item.pastPtoStatus.totalMinutesPotential);
        item.isYearlyPtoTextDanger = item.pastPtoStatus.currentYearMinutesUsage + (item.status !== 'APPROVED' ? item.duration : 0)  > item.pastPtoStatus.currentYearMinutesPotential;
        item.isAllTimePtoTextDanger = item.pastPtoStatus.totalMinutesUsage + (item.status !== 'APPROVED' ? item.duration : 0) > item.pastPtoStatus.totalMinutesPotential;
      }
      $scope.selectionLogic.initItem(item);
    });
  };

  const initTable = function (items) {
    initSelection();

    const hasItems = items && items.length;
    if (hasItems) {
      mapItems(items);
    }

    const options = {
      count: 25,
    };
    $scope.tableParams = new NgTableParams(options, {
      dataset: items,
    });
  };


  ptoScope = () => $scope;

  const releasePtoApprovalsFilter = provideAiCapability("ptoApprovals.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);
        const asDate = (text) => {
            const parts = String(text).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                throw new Error("Could not read the date " + text + ".");
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        };
        const pickAll = (available, requested, what) =>
            asList(requested).map((one) => {
                const found = (available ?? []).find(
                    (item) =>
                        String(item.id) === String(one) ||
                        String(item.label ?? "").toLowerCase() === String(one).toLowerCase() ||
                        String(item.text ?? "").toLowerCase() === String(one).toLowerCase()
                );

                if (found === undefined) {
                    throw new Error("This agency has no " + what + " " + one + ".");
                }

                return found;
            });

        if (args.from !== undefined) {
            $scope.pageManager.searchParams.from.value = asDate(args.from);
        }

        if (args.to !== undefined) {
            $scope.pageManager.searchParams.to.value = asDate(args.to);
        }

        if (args.from !== undefined || args.to !== undefined) {
            applied.push([args.from, args.to].filter(Boolean).join(" to "));
        }

        if (args.status !== undefined) {
            const statuses = pickAll($scope.statuses, args.status, "PTO status");

            $scope.extraFilters.statuses = statuses.map((status) => ({ id: status.id }));
            $scope.pageManager.searchParams.statuses.value = $scope.extraFilters.statuses;
            applied.push(statuses.map((status) => status.text).join(" and "));
        }

        if (args.office !== undefined) {
            const offices = pickAll($scope.officeDataManager?.getOffices(), args.office, "office");

            $scope.pageManager.searchParams.selectedOffices.value = offices;
            applied.push(offices.map((office) => office.name).join(" and "));
        }

        if (args.search !== undefined) {
            $scope.globalFilter.val = String(args.search);
            applied.push('"' + String(args.search) + '"');
        }

        $scope.loadItems();

        return applied.length === 0
            ? "Re-ran the PTO approvals search"
            : "Showing PTO approvals: " + applied.join(", ");
  });

  $scope.$on("$destroy", releasePtoApprovalsFilter);

  $scope.loadItems = function () {
    $scope.globalFilter.val = "";
    $scope.pageManager.executeSearch().then(
      ({ data }) => {
        $scope.caregiverPtos = data.caregiverPtos;
        initTable($scope.caregiverPtos);
      },
      (error) => {
        toaster.pop("error", "Failed to load caregiver PTO approval data");
      }
    );
  };

  $scope.updateRowStatus = (id, status) => {
    const statuses = [{ id, status }];

    updateStatuses(statuses);
  }

  $scope.updateBulkStatuses = (newStatus) => {
    const selectedRows = $scope.selectionLogic.getSelectedItems();
    if (selectedRows.length === 0) {
      return;
    }

    const statuses = selectedRows.map(row => ({ id: row.id, status: newStatus }));

    updateStatuses(statuses);
  }

  const updateStatuses = (statuses) => {
    const url = wildcard(
      "agencies/:agencyId/agency_members/:agencyMemberId/caregiver_pto_instances",
      $rootScope.agencyId,
      $rootScope.agencyMemberId
    );
    DatabaseApi.put(url, { statuses: statuses }
    ).then(
      function (res) {
        toaster.pop(
          "success",
          "Success",
          "Edited PTO statuses"
        );
        $scope.loadItems();
      }).catch(function (err) {
        toaster.pop(
          "error",
          "Something went wrong",
          "Failed to update PTO Statuses"
        );
      }
      );

  }

  $scope.openPTOInstanceGeneralNotes = ({ id, caregiverName, visitInstance: { startTime } }) => {
    const modalTitle = caregiverName + "'s " + $filter('mfShortDate')(startTime, "UTC") + " PTO"
    const notesModalConfig = {
      profileId: id,
      profileName: modalTitle
    };
    ptoNotesModalService.handleNotesModalOpen(notesModalConfig);
    ptoNotesModalService.setHandlePostNote(() => {});
    ptoNotesModalService.setHandleDeleteNote(() => {});
  }

  $scope.applyGlobalSearch = function (term) {
    const filter = { $: term };
    if ($scope.tableParams) {
      angular.extend($scope.tableParams.filter(), filter);
      $timeout(() => $scope.selectionLogic.initItemsCollection($scope.tableParams.data));
    }
  };

  /* Open PTO edit modal */
  $scope.clickTableRow = function (row) {
    const newScope = $scope.$new();

    newScope.ptoEventForm = row;
    newScope.ptoEventForm.pto = row;
    newScope.ptoEventForm.visitInstance.durationInMinutes = row.duration;
    const startTime = newScope.ptoEventForm.visitInstance.startTime;
    const endTime = newScope.ptoEventForm.visitInstance.endTime;
    newScope.ptoEventForm.visitInstance.originalStart = moment(startTime.toJSON ? startTime.toJSON() : startTime);
    newScope.ptoEventForm.visitInstance.originalEnd = moment(endTime.toJSON ? endTime.toJSON() : endTime);
    newScope.ptoEventForm.label = { id: row.labelId, text: row.labelText };
    // One patient by id, filled in when it arrives; the title is simply blank
    // until then, as it was while the dictionary was still downloading.
    newScope.ptoEventForm.visitInstance.titleName = '';
    patientLookupService.byId(newScope.ptoEventForm.visitInstance.patientId).then((patient) => {
      newScope.ptoEventForm.visitInstance.titleName = patient === null ? '' : patient.displayName;
    });
    newScope.visitInstances = [newScope.ptoEventForm.visitInstance];
    newScope.viewModeOnly = true;

    newScope.caregiverId = row.caregiverId;

    const modalInstance = $uibModal.open({
      templateUrl: "admin/views/visit-instance-pto-modal.html",
      size: "md",
      controller: "visitInstancePTOModalCtrl",
      windowTopClass: "PTO-modal",
      scope: newScope,
    });
    modalInstance.result.then((res) => {
      if (res === "EDIT_SUCCESS" || res === "REMOVE_SUCCESS") {
        $scope.loadItems();
      }
    });
  };

  $scope.goToPayrollBatchPage = (payrollBatchId) => {
    $state.go('app.payroll.batch', { id: payrollBatchId });
  };

  const loadItemsIfAssetsAreReady = () => {
    if ($scope.areAssetsReady) {
      return;
    }
    checkAssets();
    if ($scope.areAssetsReady) {
      $scope.loadItems();
    }
  };
  
  const checkAssets = () => {
    $scope.initializeMap = DatabaseApi.entitiesInitializeMap();
    $scope.caregiverPTOLabels = DatabaseApi.caregiverPtoLabels();
    $scope.payrollCodes = DatabaseApi.activePayrollCodes();
    const caregiverPtoLabelsState = $scope.initializeMap['caregiverPtoLabels'];
    const payrollCodesState = $scope.initializeMap['payrollCodes'];
    // Patients are resolved per row now, so they are not an asset to wait for.
    if (caregiverPtoLabelsState && payrollCodesState) {
      $scope.areAssetsReady = true;
    } else if (!caregiverPtoLabelsState) {
      DatabaseApi.getPtoLabels();
    }
  };

  $rootScope.$on("got_pto_labels", () => {
    loadItemsIfAssetsAreReady();
  });
  
  $rootScope.$on("got_payroll_codes", () => {
    loadItemsIfAssetsAreReady();
  });
  
  initialize();
};
